/**
 * Right-rail family cart. Flat summary — item count beside the gross
 * subtotal, the school-covered subsidy when one applies, a tax row, and
 * the tax-inclusive total the wallet is actually charged — so the
 * arithmetic (cost − subsidy + tax = total) is explicit and no single
 * amount is ambiguous. Mirrors the school `CartPanel`.
 */

import { Button } from '@ordo/ui/button'
import { cn } from '@ordo/ui/cn'
import { useDestructiveConfirm } from '@ordo/ui/destructive-confirm'
import { Trash2 } from 'lucide-react'
import type { ReactElement } from 'react'

import { useCartPricePreview } from '@/components/orders/family/use-cart-price-preview'
import { subtotalCents, totalQty } from '@/lib/family-orders/cart'
import { previewPayableCents } from '@/lib/family-orders/price-preview'
import { formatCents } from '@/lib/school-orders/money'
import { useAllCartLines, useFamilyCart } from '@/stores/family-cart'

export interface FamilyCartPanelProps {
  onCheckout: () => void
  submitting?: boolean
  className?: string
}

export function FamilyCartPanel({ onCheckout, submitting, className }: FamilyCartPanelProps): ReactElement {
  // Every campus's lines — the family cart aggregates all children.
  const lines = useAllCartLines()
  const clearLocation = useFamilyCart((s) => s.clearLocation)
  const confirmClear = useDestructiveConfirm()

  // Hunter (launch-gap 09): the family flow had NO clear-cart at all —
  // only per-line steppers. Clears every campus bucket the panel shows
  // (the store op tombstones each line, so the server copy drains too).
  const clearCart = async (): Promise<void> => {
    const ok = await confirmClear({
      title: 'Clear your cart?',
      body: 'This removes every meal from your cart across all days. Orders you already placed are not affected.',
      confirmLabel: 'Clear cart',
    })
    if (!ok) {
      return
    }
    for (const locationId of new Set(lines.map((l) => l.locationId))) {
      clearLocation(locationId)
    }
  }

  const qty = totalQty(lines)
  // What the family will actually pay (net of subsidies), from the
  // shared server preview; gross store subtotal only while it loads.
  const preview = useCartPricePreview()
  const totals = preview.totals
  const gross = totals ? totals.grossCents : subtotalCents(lines)
  const total = totals ? previewPayableCents(totals) : subtotalCents(lines)
  const currency = lines.find((l) => l.currency)?.currency ?? 'USD'

  return (
    <div className={cn('flex flex-col rounded-xl border bg-card p-4', className)}>
      <h2 className="shrink-0 text-base font-semibold text-foreground">Cart</h2>
      {qty === 0 ? (
        <div className="mt-3 grid place-items-center rounded-lg bg-muted/50 py-6 text-sm text-muted-foreground">
          Your cart is empty
        </div>
      ) : (
        <>
          <dl className="mt-3 flex shrink-0 flex-col gap-1.5 text-sm">
            <div className="flex items-baseline justify-between gap-2">
              <dt className="text-muted-foreground">
                {qty} {qty === 1 ? 'item' : 'items'}
              </dt>
              <dd className="tabular-nums text-foreground">{gross !== null ? formatCents(gross, currency) : '—'}</dd>
            </div>
            {totals && totals.subsidyCents > 0 ? (
              <div className="flex items-baseline justify-between gap-2">
                <dt className="text-muted-foreground">School covers</dt>
                <dd className="tabular-nums text-emerald-700 dark:text-emerald-300">
                  −{formatCents(totals.subsidyCents, currency)}
                </dd>
              </div>
            ) : null}
            {/* Always itemized — "$0.00" for a taxless campus, "—" only
                while the server preview loads — so the total below reads
                explicitly as cost + tax. */}
            <div className="flex items-baseline justify-between gap-2">
              <dt className="text-muted-foreground">Tax</dt>
              <dd className="tabular-nums text-foreground">{totals ? formatCents(totals.taxCents, currency) : '—'}</dd>
            </div>
            <div className="mt-0.5 flex items-baseline justify-between gap-2 border-t pt-2">
              <dt className="font-medium text-foreground">Total</dt>
              <dd className="font-semibold tabular-nums text-foreground">
                {total !== null ? formatCents(total, currency) : '—'}
              </dd>
            </div>
          </dl>
          <Button className="mt-4 w-full shrink-0" onClick={onCheckout} disabled={submitting}>
            {submitting ? 'Submitting…' : 'Review & Checkout'}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => void clearCart()}
            disabled={submitting}
            className="mt-1 w-full shrink-0 text-destructive hover:bg-destructive/10 hover:text-destructive">
            <Trash2 className="h-4 w-4" />
            Clear cart
          </Button>
        </>
      )}
    </div>
  )
}
